/**
 * Recs slice — RECOMMENDATIONS (DESIGN-CHANGELOG 2026-09-03 "Sending (second
 * pass)" and "RECOMMENDATIONS: the fifth door"; docs/porting/AA.md phase 3;
 * migration 3062).
 *
 * `sends` is every recommendation I am party to in the active crew — sent to
 * me or by me — as the read policy hands it back, replaced whole on every
 * read. CREW-SCOPED (the prototype keeps it in CREW_SLICE): a switch blanks it
 * with the rest of the party's state.
 *
 * Plus the view state of the two surfaces built on it: the RECOMMEND modal /
 * sheet (`sdId` is the game, `0` = closed; `sdTo` the checked recipients as
 * user keys; `sdNote`; `sdEdit`) and the RECOMMENDATIONS page (`rcTab`, the
 * armed REVOKE `rcArm`).
 *
 * WRITES ARE NOT PATCHED ROW BY ROW. A send is one game, N recipients, one
 * note, and on the server it is one function call that replaces, edits or
 * revokes the whole group atomically (3062). The optimistic step models the
 * same group op on `sends` — the same rules, so the screen after the click
 * is the screen after the refetch — and `mutate` rolls the whole array back
 * on failure. Ids for optimistic rows are provisional; the next read brings
 * the real ones, and every key in the page is `(from, gid)` or a row id that
 * only has to be unique for one render.
 */

import type { StateCreator } from 'zustand';

import type { GameId, SendRec, SendStatus } from '../../lib/types.ts';
import { R_ARM_MS } from '../../lib/round.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { QdStore } from '../types.ts';

export type RecsTabKey = 'in' | 'out';

/** The note's bound (3062's CHECK; the prototype's `maxLength="160"`). */
export const SEND_NOTE_MAX = 160;

export interface RecsSlice {
  sends: SendRec[];

  /** RECOMMEND — the game the modal is open on; `0` = closed */
  sdId: GameId;
  sdTo: string[];
  sdNote: string;
  sdEdit: boolean;
  /** RECOMMENDATIONS page: TO YOU / FROM YOU, and the armed REVOKE (a game id) */
  rcTab: RecsTabKey;
  rcArm: GameId;

  /** Open RECOMMEND on a game, blank. Inert with nobody else in the party. */
  sdOpen: (id: GameId) => void;
  /** Reopen on a game I already sent: recipients pre-checked, note prefilled. */
  sdOpenEdit: (id: GameId) => void;
  closeSend: () => void;
  sdToggle: (userKey: string) => void;
  /** THE WHOLE PARTY: everyone but me; pressed with everyone in, clears. */
  sdAll: () => void;
  sdSetNote: (v: string) => void;
  /** SEND ▸ / SAVE ▸ / REVOKE ▸ — whichever the modal is showing. */
  sdSend: () => Promise<void>;
  /** REVOKE from the page (armed): every recipient loses it, silently. */
  sdRevoke: (id: GameId) => Promise<void>;

  /** The recipient's answer: `''` BRING IT BACK · `'x'` NOT FOR ME · `'added'`. */
  sendSet: (id: string, st: SendStatus) => Promise<void>;
  /** ADD TO BACKLOG ▸ on a card: shelves it AND answers `added`. */
  sendAdd: (id: string) => Promise<void>;

  setRcTab: (t: RecsTabKey) => void;
  /** Arm REVOKE on one FROM YOU row (`0` disarms); 5 s and click-away, like `bkArmSet`. */
  rcArmSet: (id: GameId) => void;
}

let provisional = 0;

export const createRecsSlice: StateCreator<QdStore, [], [], RecsSlice> = (set, get) => {
  let armTimer: ReturnType<typeof setTimeout> | null = null;
  let armAway: ((ev: MouseEvent) => void) | null = null;
  const disarmListener = (): void => {
    if (armAway && typeof document !== 'undefined') document.removeEventListener('click', armAway, true);
    armAway = null;
  };

  return {
    sends: [],
    sdId: 0,
    sdTo: [],
    sdNote: '',
    sdEdit: false,
    rcTab: 'in',
    rcArm: 0,

    sdOpen: (id) => {
      const s = get();
      // Nobody to send to: the control stays and its tip says so (design).
      if (s.users.length < 2) return;
      set({ sdId: id, sdTo: [], sdNote: '', sdEdit: false });
    },
    sdOpenEdit: (id) => {
      const s = get();
      const me = s.me();
      if (!me) return;
      const mine = s.sends.filter((r) => r.from === me.k && r.gid === id);
      if (mine.length === 0) return;
      // The newest row's note — one note per group, but a re-send to one
      // recipient may have reworded it, and the newest is the one you meant.
      const newest = mine.reduce((a, b) => (b.at > a.at ? b : a));
      set({ sdId: id, sdTo: mine.map((r) => r.to), sdNote: newest.note, sdEdit: true });
    },
    closeSend: () => set({ sdId: 0 }),
    sdToggle: (k) =>
      set((st) => ({ sdTo: st.sdTo.includes(k) ? st.sdTo.filter((x) => x !== k) : [...st.sdTo, k] })),
    sdAll: () => {
      const s = get();
      const me = s.me();
      const all = s.users.filter((u) => !me || u.k !== me.k).map((u) => u.k);
      const allOn = all.length > 0 && all.every((k) => s.sdTo.includes(k));
      set({ sdTo: allOn ? [] : all });
    },
    sdSetNote: (v) => set({ sdNote: v.slice(0, SEND_NOTE_MAX) }),

    sdSend: async () => {
      const s = get();
      const me = s.me();
      const id = s.sdId;
      if (!me || !id) return;
      const to = [...s.sdTo];
      const note = s.sdNote.trim();
      const edit = s.sdEdit;
      // The modal's own gate, restated: a send needs both; an edit with nobody
      // left is a revoke and needs no note.
      if (to.length === 0 && !edit) return;
      if (to.length > 0 && note === '') return;
      const g = s.gById(id);
      const gn = g ? ' · ' + g.n.toUpperCase() : '';
      const at = Date.now();
      // Close first: a slow commit must not hold the form open over the page
      // that already shows the result.
      set({ sdId: 0 });
      const toast =
        edit && to.length === 0
          ? 'RECOMMENDATION REVOKED' + gn
          : edit
            ? 'RECOMMENDATION UPDATED' + gn
            : to.length === 1
              ? 'SENT TO ' + (s.users.find((u) => u.k === to[0])?.name ?? '').toUpperCase() + gn
              : 'SENT TO ' + to.length + ' PLAYERS' + gn;
      await mutate(storeApi(get, set), {
        key: 'send:' + id,
        optimistic: (st) => {
          const mine = (r: SendRec) => r.from === me.k && r.gid === id;
          const fresh = (k: string): SendRec => ({ id: 'p' + ++provisional, from: me.k, to: k, gid: id, note, at, st: '' });
          if (edit && to.length === 0) return { sends: st.sends.filter((r) => !mine(r)) };
          if (edit) {
            const kept = st.sends
              .filter((r) => !mine(r) || to.includes(r.to))
              .map((r) => (mine(r) ? { ...r, note } : r));
            const have = new Set(kept.filter(mine).map((r) => r.to));
            return { sends: [...kept, ...to.filter((k) => !have.has(k)).map(fresh)] };
          }
          // A send replaces the named recipients' cards whole.
          return { sends: [...st.sends.filter((r) => !(mine(r) && to.includes(r.to))), ...to.map(fresh)] };
        },
        commit: () => s.repo.setSends(id, to, note, edit),
        toast,
      });
    },

    sdRevoke: async (id) => {
      const s = get();
      const me = s.me();
      if (!me) return;
      if (s.rcArm === id) get().rcArmSet(0);
      const g = s.gById(id);
      await mutate(storeApi(get, set), {
        key: 'send:' + id,
        optimistic: (st) => ({ sends: st.sends.filter((r) => !(r.from === me.k && r.gid === id)) }),
        commit: () => s.repo.setSends(id, [], '', true),
        toast: 'RECOMMENDATION REVOKED' + (g ? ' · ' + g.n.toUpperCase() : ''),
      });
    },

    sendSet: async (id, st) => {
      const s = get();
      const row = s.sends.find((r) => r.id === id);
      if (!row) return;
      await mutate(storeApi(get, set), {
        key: 'sendst:' + id,
        optimistic: (cur) => ({ sends: cur.sends.map((r) => (r.id === id ? { ...r, st } : r)) }),
        commit: () => s.repo.answerSend(id, st),
      });
    },

    sendAdd: async (id) => {
      const s = get();
      const row = s.sends.find((r) => r.id === id);
      if (!row) return;
      // The shelf first (its own toast, `ON YOUR BACKLOG · <GAME>`), then the
      // answer — two writes, two keys, each rolls back alone.
      await s.soloAdd(row.gid);
      await get().sendSet(id, 'added');
    },

    setRcTab: (t) => set({ rcTab: t }),

    rcArmSet: (id) => {
      if (armTimer) clearTimeout(armTimer);
      armTimer = null;
      disarmListener();
      set({ rcArm: id });
      if (!id) return;
      armTimer = setTimeout(() => {
        armTimer = null;
        if (get().rcArm === id) set({ rcArm: 0 });
      }, R_ARM_MS);
      if (typeof document !== 'undefined') {
        armAway = (ev) => {
          const t = ev.target as Element | null;
          if (t && typeof t.closest === 'function' && t.closest('[data-arm]')) return;
          if (get().rcArm) set({ rcArm: 0 });
          disarmListener();
        };
        setTimeout(() => {
          if (armAway) document.addEventListener('click', armAway, true);
        }, 0);
      }
    },
  };
};
